// Host Me — Push service-worker registration
// Guest Comms stage 1, Inc 5b · D78, D79, D86, D87.
//
// WHAT REPLACED WHAT. This module used to be `unregisterAndCleanup()`, called on every app boot to
// destroy the old CACHING service worker — which intercepted API calls and masked real backend
// errors. That worker is gone and must not come back; see the warning at the top of `public/sw.js`.
// What is here now registers a worker that does exactly one thing: receive push notifications.
//
// TWO RULES THIS FILE ENFORCES, both learned rather than assumed:
//
// 1. REGISTRATION HAPPENS ON A USER GESTURE, NEVER ON BOOT. `Notification.requestPermission()` fired
//    on page load is the fastest known route to a permanent `denied` — a state no code can undo, on
//    any browser, ever. So nothing here runs by itself: every function below is reached from the
//    button in Settings that the operator deliberately pressed.
//
// 2. THE BROWSER AND THE SERVER CAN DISAGREE, silently and often. A browser keeps its subscription
//    when our row is deleted; our row survives when the browser drops its subscription; and both
//    look fine while a VAPID key rotation has made the pair useless. That is D87's "the hardest
//    problem in the whole notification design is subscriptions, not delivery". `subscribeThisBrowser`
//    therefore RE-REGISTERS rather than short-circuiting on an existing subscription, and drops a
//    subscription bound to a different application key rather than trusting it.

const SW_PATH = '/sw.js';

// How long to wait for the worker to become ACTIVE before giving up. Generous — activation is
// normally instant — but bounded, because the alternative is an indefinite hang. See registerPushWorker.
const READY_TIMEOUT_MS = 10000;

/**
 * This browser's own device id, as issued by `POST /notifications/subscriptions`.
 *
 * WHY IT IS STORED AT ALL. When an operator taps a notification we want to record that THIS handset
 * produced a real human open (D87's `lastPushDrivenOpen`) — but the only thing that identifies the
 * handset is its push endpoint, which is a bearer capability and must never come back to the client.
 * So the server issues an opaque id instead, and the browser keeps it.
 *
 * NOT A SECRET and not an authenticator: it is already returned by `GET /me`, and on its own it can
 * do nothing — every use of it is filtered by the session's own `userId` server-side.
 *
 * `localStorage`, not `sessionStorage`: the whole point is that it survives the phone being locked
 * overnight and the tab being closed, which is precisely when a push matters.
 */
const DEVICE_ID_KEY = 'pushDeviceId';

export const getStoredDeviceId = () => {
  try { return localStorage.getItem(DEVICE_ID_KEY) || null; } catch (_) { return null; }
};

export const setStoredDeviceId = (id) => {
  try {
    if (id) localStorage.setItem(DEVICE_ID_KEY, String(id));
    else localStorage.removeItem(DEVICE_ID_KEY);
  } catch (_) {
    // Private mode, or storage full. The id is an optimisation for one metric — losing it costs the
    // device half of the open signal and nothing else, so it must never throw into a UI flow.
  }
};

/** Does this browser have the three APIs push needs at all? */
export const pushSupported = () =>
  typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

/** 'granted' | 'denied' | 'default' | 'unsupported' — reported verbatim, never guessed. */
export const permissionState = () => {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
};

/**
 * iOS/iPadOS, including an iPad reporting a Macintosh UA in desktop mode — which is why `maxTouchPoints`
 * is part of the test rather than the UA alone.
 */
export const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
};

/**
 * Is the app running as an installed/standalone app rather than a browser tab?
 *
 * This is not cosmetic on iOS: Safari exposes the Push API **only** to a page added to the Home
 * Screen. In a normal iOS tab `pushSupported()` is false and no amount of code changes that — the
 * install is a manual, per-device human step that cannot be prompted (D78's platform reality).
 */
export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  // `navigator.standalone` is the iOS-specific signal; the media query covers everyone else.
  return window.navigator.standalone === true
    || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
};

/**
 * The VAPID public key travels as base64url text and `pushManager.subscribe` wants raw bytes.
 * Standard conversion — padding restored, URL-safe alphabet mapped back.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/** The subscription's keys as the wire format our API expects. */
function serializeSubscription(subscription) {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth }
  };
}

/**
 * Register the push worker and wait until it is actually controlling.
 *
 * `navigator.serviceWorker.ready` matters: `register()` resolves as soon as the file is fetched, but
 * `pushManager` needs an ACTIVE worker. Subscribing too early fails with an error that reads like a
 * permission problem and is not one.
 */
export async function registerPushWorker() {
  if (!pushSupported()) return null;
  await navigator.serviceWorker.register(SW_PATH);

  // ⚠️ `ready` NEVER REJECTS AND NEVER TIMES OUT. If activation stalls — a conflicting registration
  // already holding scope `/`, which is not hypothetical (one was found on a test origin during this
  // build) — the await hangs forever, the caller's `finally` never runs, and the operator is left
  // with a button stuck on «جاري التفعيل...» and no recovery but a page reload. The backend half of
  // this capability learned the same lesson: no outbound call there is unbounded either.
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => setTimeout(() => reject(new Error('service worker activation timed out')), READY_TIMEOUT_MS))
  ]);
}

/** This browser's current subscription, if it has one. Read-only — used to render state. */
export async function getExistingSubscription() {
  if (!pushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (!registration) return null;
    return registration.pushManager.getSubscription();
  } catch (_) {
    return null;
  }
}

/**
 * Ask for permission, register the worker, and produce a subscription for the given VAPID key.
 *
 * ⚠️ CALL THIS ONLY FROM A CLICK HANDLER. Browsers increasingly require a transient user activation
 * for `requestPermission()`, and the ones that do not still punish an unprompted ask.
 *
 * @param {string} vapidPublicKey from GET /api/v1/notifications/config
 * @returns {Promise<{ok:boolean, reason?:string, subscription?:{endpoint:string, keys:object}}>}
 *          Reasons are machine strings the caller maps to Arabic — this module renders nothing.
 */
export async function subscribeThisBrowser(vapidPublicKey) {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  if (!vapidPublicKey) return { ok: false, reason: 'not_configured' };

  // Asking again while denied resolves INSTANTLY with 'denied' and shows no prompt, so a button that
  // retries looks broken. Told apart here so the panel can say where the real switch lives.
  if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: permission === 'denied' ? 'denied' : 'dismissed' };
  }

  try {
    const registration = await registerPushWorker();
    if (!registration) return { ok: false, reason: 'unsupported' };

    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    // An existing subscription is NOT reused blindly. If it was created under a different VAPID key
    // — a rotation, or a browser that once pointed at staging — it is cryptographically dead and
    // every push to it fails with a status that never prunes it (this is R11, and the server-side
    // `vapidKeyFingerprint` is the other half of the same defence). Dropping it here is what makes
    // "press the button again" a real fix rather than a ritual.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      const sameKey = existing.options
        && existing.options.applicationServerKey
        && new Uint8Array(existing.options.applicationServerKey).toString() === applicationServerKey.toString();
      if (!sameKey) {
        await existing.unsubscribe();
      } else {
        // Same key: still re-report it to the server, because the row may have been deleted (a 410
        // from a temporary failure, an offboarding, a dropped collection) while the browser kept its
        // half. Re-registering an identical subscription is idempotent server-side.
        return { ok: true, subscription: serializeSubscription(existing) };
      }
    }

    const subscription = await registration.pushManager.subscribe({
      // Required by every browser that implements Web Push. A silent subscription (visible:false) is
      // not permitted, and we would not want one — a notification nobody sees is not a notification.
      userVisibleOnly: true,
      applicationServerKey
    });

    return { ok: true, subscription: serializeSubscription(subscription) };
  } catch (err) {
    console.error('Push subscribe failed:', err);
    return { ok: false, reason: 'subscribe_failed' };
  }
}

/**
 * Drop this browser's half of the pairing.
 *
 * Called alongside deleting the server row when the operator removes THIS device, so the two halves
 * stay in step. Failing is not surfaced: the server row is the one that decides whether anything is
 * sent, and a stale browser subscription with no row behind it is inert.
 */
export async function unsubscribeThisBrowser() {
  try {
    const subscription = await getExistingSubscription();
    if (subscription) await subscription.unsubscribe();
    return true;
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
    return false;
  }
}
